import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  useActive,
  useRefreshTracking,
  useUpdateForm5106,
  useManualTracking,
  useMarkManual,
  useRepostTracking,
  useCancelDropship,
  useCompleteDropship,
} from '@/hooks/useActive'
import type { ActiveOrder } from '@/lib/schemas/active'
import {
  statusBadge,
  carrierLabel,
  trackingUrl,
  formatRefreshSummary,
  isRetired,
  CARRIERS,
} from '@/lib/active-status'

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(d)
}

type ModalType = 'manual-tracking' | 'mark-manual' | 'repost' | 'cancel' | 'complete'
type ModalState = { type: ModalType; order: ActiveOrder } | null

const TH =
  'px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase whitespace-nowrap'

export default function ActiveDropships() {
  const [includeRetired, setIncludeRetired] = useState(false)
  const { data, isLoading, isError, error, refetch, isFetching } = useActive(includeRetired)
  const refresh = useRefreshTracking()
  const form5106 = useUpdateForm5106()
  const [modal, setModal] = useState<ModalState>(null)

  const orders = data?.orders ?? []
  const globalRefreshing = refresh.isPending && refresh.variables === undefined

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">Active dropships</h1>
          {isFetching && !isLoading && (
            <span className="text-xs text-gray-400">Refreshing…</span>
          )}
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={includeRetired}
              onChange={(e) => setIncludeRetired(e.target.checked)}
            />
            Show completed &amp; cancelled
          </label>
        </div>
        <button
          type="button"
          onClick={() => refresh.mutate(undefined)}
          disabled={refresh.isPending}
          className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {globalRefreshing ? 'Refreshing tracking…' : 'Refresh tracking'}
        </button>
      </div>

      {refresh.isSuccess && refresh.data && (
        <p className="text-xs text-gray-600">{formatRefreshSummary(refresh.data)}</p>
      )}
      {refresh.isError && (
        <p role="alert" className="text-xs text-red-600">
          {refresh.error instanceof Error ? refresh.error.message : 'Refresh failed.'}
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading active dropships…</p>
      ) : isError ? (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Failed to load active dropships.'}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white"
          >
            Retry
          </button>
        </div>
      ) : orders.length === 0 ? (
        <p className="text-sm text-gray-500">
          {includeRetired ? 'No dropship orders' : 'No active dropships'}
        </p>
      ) : (
        <div className="overflow-x-auto border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className={TH}>Order</th>
                <th className={TH}>Reference</th>
                <th className={TH}>Customer</th>
                <th className={TH}>Status</th>
                <th className={TH}>KH status</th>
                <th className={TH}>Form 5106</th>
                <th className={TH}>Tracking</th>
                <th className={TH}>Submitted</th>
                <th className={TH}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <Row
                  key={order.dropship_order_id}
                  order={order}
                  refresh={refresh}
                  form5106={form5106}
                  onOpenModal={(type) => setModal({ type, order })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal?.type === 'manual-tracking' && (
        <ManualTrackingModal order={modal.order} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'mark-manual' && (
        <MarkManualModal order={modal.order} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'repost' && (
        <RepostModal order={modal.order} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'cancel' && (
        <CancelModal order={modal.order} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'complete' && (
        <CompleteModal order={modal.order} onClose={() => setModal(null)} />
      )}
    </div>
  )
}

// ---- Table row ----

function Row({
  order,
  refresh,
  form5106,
  onOpenModal,
}: {
  order: ActiveOrder
  refresh: ReturnType<typeof useRefreshTracking>
  form5106: ReturnType<typeof useUpdateForm5106>
  onOpenModal: (type: ModalType) => void
}) {
  const badge = statusBadge(order.status)
  const retired = isRetired(order.status)
  const rowRefreshing =
    refresh.isPending && refresh.variables === order.dropship_order_id

  return (
    <tr className="border-b border-gray-100 align-top last:border-b-0">
      <td className="px-3 py-2">
        <Link
          to={`/active/${order.dropship_order_id}`}
          className="font-medium tabular-nums hover:underline"
        >
          #{order.order_number}
        </Link>
        {order.internal_note && (
          <div className="mt-0.5 max-w-xs text-xs text-gray-400">{order.internal_note}</div>
        )}
      </td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-gray-600">
        {order.reference_client}
      </td>
      <td className="px-3 py-2">
        {order.customer_name}
        {order.is_vip_order && (
          <span title="VIP" aria-label="VIP" className="ml-1">
            ⭐
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <span
          className={`inline-block border px-2 py-0.5 text-xs ${badge.className}`}
          title={order.status === 'ERROR' ? order.error_message ?? undefined : undefined}
        >
          {badge.label}
        </span>
        {order.status === 'ERROR' && order.error_message && (
          <p className="mt-1 max-w-xs text-xs text-red-700">{order.error_message}</p>
        )}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{order.kh_status}</td>
      <td className="px-3 py-2">
        {order.form_5106_status === 'ON_FILE' ? (
          <span className="inline-block border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-800">
            On file
          </span>
        ) : (
          <Dropdown
            ariaLabel="Form 5106 actions"
            align="left"
            triggerClassName="cursor-pointer"
            label={
              <span className="inline-block border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                Pending
              </span>
            }
          >
            {(close) => (
              <MenuItem
                onClick={() => {
                  form5106.mutate({ id: order.dropship_order_id, action: 'sent' })
                  close()
                }}
              >
                Mark sent
              </MenuItem>
            )}
          </Dropdown>
        )}
      </td>
      <td className="px-3 py-2">
        {order.trackings.length === 0 ? (
          <span className="text-gray-400">—</span>
        ) : (
          <ul className="space-y-0.5">
            {order.trackings.map((t) => {
              const url = trackingUrl(t.transporter, t.tracking_number)
              return (
                <li key={t.id} className="whitespace-nowrap">
                  {url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tabular-nums underline hover:text-black"
                    >
                      {t.tracking_number}
                    </a>
                  ) : (
                    <span className="tabular-nums">{t.tracking_number}</span>
                  )}
                  <span className="ml-1 text-xs text-gray-500">{carrierLabel(t.transporter)}</span>
                  {t.source === 'MANUAL' && (
                    <span className="ml-1 border border-gray-300 px-1 text-[10px] text-gray-600">
                      MANUAL
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="tabular-nums">{formatDate(order.submitted_at)}</div>
        <div className="text-xs text-gray-400">by {order.submitted_by}</div>
      </td>
      <td className="px-3 py-2">
        {retired ? (
          <span className="text-gray-300">—</span>
        ) : (
          <Dropdown ariaLabel="Order actions" label={<span aria-hidden="true">⋮</span>}>
            {(close) => (
              <>
                <MenuItem
                  onClick={() => {
                    refresh.mutate(order.dropship_order_id)
                    close()
                  }}
                >
                  {rowRefreshing ? 'Refreshing…' : 'Refresh this order'}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    onOpenModal('manual-tracking')
                    close()
                  }}
                >
                  Enter tracking manually
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    onOpenModal('mark-manual')
                    close()
                  }}
                >
                  Mark as manually submitted
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    onOpenModal('repost')
                    close()
                  }}
                >
                  Re-post tracking
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    onOpenModal('complete')
                    close()
                  }}
                >
                  Mark complete
                </MenuItem>
                <MenuItem
                  destructive
                  onClick={() => {
                    onOpenModal('cancel')
                    close()
                  }}
                >
                  Mark as cancelled
                </MenuItem>
              </>
            )}
          </Dropdown>
        )}
      </td>
    </tr>
  )
}

// ---- Dropdown / menu primitives ----

function Dropdown({
  label,
  ariaLabel,
  triggerClassName,
  align = 'right',
  children,
}: {
  label: ReactNode
  ariaLabel: string
  triggerClassName?: string
  align?: 'left' | 'right'
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{
    left?: number
    right?: number
    top?: number
    bottom?: number
  } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const close = () => setOpen(false)

  // Position a `fixed` panel from the trigger's viewport rect so it escapes the
  // table's `overflow-x-auto` clipping container. Opens upward when there isn't
  // room below.
  const openMenu = () => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const estimatedHeight = 240
    const openUp = rect.bottom + estimatedHeight > window.innerHeight
    const next: { left?: number; right?: number; top?: number; bottom?: number } = {}
    if (align === 'right') {
      next.right = window.innerWidth - rect.right
    } else {
      next.left = rect.left
    }
    if (openUp) {
      next.bottom = window.innerHeight - rect.top + 4
    } else {
      next.top = rect.bottom + 4
    }
    setCoords(next)
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        onClick={() => (open ? close() : openMenu())}
        className={triggerClassName ?? 'px-2 py-1 text-gray-500 hover:text-black'}
      >
        {label}
      </button>
      {open && coords && (
        <>
          <div className="fixed inset-0 z-40" aria-hidden="true" onClick={close} />
          <div
            role="menu"
            className="fixed z-50 min-w-max border border-gray-300 bg-white py-1 shadow-lg"
            style={coords}
          >
            {children(close)}
          </div>
        </>
      )}
    </div>
  )
}

function MenuItem({
  onClick,
  destructive,
  children,
}: {
  onClick: () => void
  destructive?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`block w-full px-3 py-2 text-left text-sm whitespace-nowrap hover:bg-gray-100 ${
        destructive ? 'text-red-700' : ''
      }`}
    >
      {children}
    </button>
  )
}

// ---- Modal shells ----

interface ModalProps {
  order: ActiveOrder
  onClose: () => void
}

function ModalShell({
  title,
  order,
  children,
}: {
  title: string
  order: ActiveOrder
  children: ReactNode
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md space-y-4 border border-gray-300 bg-white p-4 shadow-lg">
        <div>
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="text-xs text-gray-500">
            Order #{order.order_number} · {order.reference_client}
          </p>
        </div>
        {children}
      </div>
    </div>
  )
}

function ModalError({ error }: { error: unknown }) {
  return (
    <p role="alert" className="text-sm text-red-600">
      {error instanceof Error ? error.message : 'Something went wrong.'}
    </p>
  )
}

function ModalFooter({
  onClose,
  onConfirm,
  confirmLabel,
  disabled,
  destructive,
}: {
  onClose: () => void
  onConfirm: () => void
  confirmLabel: string
  disabled: boolean
  destructive?: boolean
}) {
  const confirmClass = destructive
    ? 'border border-red-700 bg-red-700 px-3 py-1 text-sm text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-200 disabled:text-gray-400'
    : 'border border-gray-800 bg-gray-900 px-3 py-1 text-sm text-white hover:bg-black disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-200 disabled:text-gray-400'
  return (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={onClose}
        className="border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:text-black"
      >
        Cancel
      </button>
      <button type="button" onClick={onConfirm} disabled={disabled} className={confirmClass}>
        {confirmLabel}
      </button>
    </div>
  )
}

const FIELD = 'w-full border border-gray-300 px-2 py-1 text-sm'
const LABEL = 'text-sm font-medium'

// ---- Manual tracking ----

function ManualTrackingModal({ order, onClose }: ModalProps) {
  const m = useManualTracking()
  const [trackingNumber, setTrackingNumber] = useState('')
  const [carrier, setCarrier] = useState(CARRIERS[0].value)
  const [postToShopify, setPostToShopify] = useState(true)

  const canSubmit = trackingNumber.trim().length > 0 && !m.isPending

  function submit() {
    m.mutate(
      {
        id: order.dropship_order_id,
        input: {
          tracking_number: trackingNumber.trim(),
          carrier,
          post_to_shopify: postToShopify,
        },
      },
      { onSuccess: onClose },
    )
  }

  return (
    <ModalShell title="Enter tracking manually" order={order}>
      <div className="space-y-1">
        <label htmlFor="mt-number" className={LABEL}>
          Tracking number
        </label>
        <input
          id="mt-number"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          className={`${FIELD} tabular-nums`}
          autoFocus
        />
      </div>
      <div className="space-y-1">
        <label htmlFor="mt-carrier" className={LABEL}>
          Carrier
        </label>
        <select
          id="mt-carrier"
          value={carrier}
          onChange={(e) => setCarrier(e.target.value)}
          className={FIELD}
        >
          {CARRIERS.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={postToShopify}
          onChange={(e) => setPostToShopify(e.target.checked)}
          className="mt-0.5"
        />
        <span>Post to Shopify order and trigger customer notification</span>
      </label>
      {m.isError && <ModalError error={m.error} />}
      <ModalFooter
        onClose={onClose}
        onConfirm={submit}
        disabled={!canSubmit}
        confirmLabel={m.isPending ? 'Saving…' : 'Save tracking'}
      />
    </ModalShell>
  )
}

// ---- Mark as manually submitted ----

function MarkManualModal({ order, onClose }: ModalProps) {
  const m = useMarkManual()
  const [reference, setReference] = useState('')
  const canSubmit = reference.trim().length > 0 && !m.isPending

  function submit() {
    m.mutate(
      { id: order.dropship_order_id, kh_reference: reference.trim() },
      { onSuccess: onClose },
    )
  }

  return (
    <ModalShell title="Mark as manually submitted" order={order}>
      <p className="text-xs text-gray-500">
        {order.status === 'ERROR'
          ? 'The KH API submission failed but KH received the order out-of-band. Enter the confirmation reference KH gave you.'
          : `Intended for orders that errored on submission. This order's status is “${statusBadge(order.status).label}”.`}
      </p>
      <div className="space-y-1">
        <label htmlFor="mm-ref" className={LABEL}>
          KH confirmation reference
        </label>
        <input
          id="mm-ref"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          className={FIELD}
          autoFocus
        />
      </div>
      {m.isError && <ModalError error={m.error} />}
      <ModalFooter
        onClose={onClose}
        onConfirm={submit}
        disabled={!canSubmit}
        confirmLabel={m.isPending ? 'Saving…' : 'Mark as submitted'}
      />
    </ModalShell>
  )
}

// ---- Re-post tracking ----

function RepostModal({ order, onClose }: ModalProps) {
  const m = useRepostTracking()
  const [shopifyOrderId, setShopifyOrderId] = useState(order.shopify_order_id)
  const canSubmit = shopifyOrderId.trim().length > 0 && !m.isPending

  function submit() {
    m.mutate(
      { id: order.dropship_order_id, shopify_order_id: shopifyOrderId.trim() },
      { onSuccess: onClose },
    )
  }

  return (
    <ModalShell title="Re-post tracking" order={order}>
      <p className="text-xs text-amber-800">
        Double-check the Shopify order ID — tracking will be posted to that order and a shipping
        notification sent to its customer.
      </p>
      <div className="space-y-1">
        <label htmlFor="rp-id" className={LABEL}>
          Correct Shopify order ID
        </label>
        <input
          id="rp-id"
          value={shopifyOrderId}
          onChange={(e) => setShopifyOrderId(e.target.value)}
          className={`${FIELD} tabular-nums`}
          autoFocus
        />
      </div>
      {m.isError && <ModalError error={m.error} />}
      <ModalFooter
        onClose={onClose}
        onConfirm={submit}
        disabled={!canSubmit}
        confirmLabel={m.isPending ? 'Re-posting…' : 'Re-post tracking'}
      />
    </ModalShell>
  )
}

// ---- Mark as cancelled ----

function CancelModal({ order, onClose }: ModalProps) {
  const m = useCancelDropship()
  const [reason, setReason] = useState('')
  const canSubmit = reason.trim().length > 0 && !m.isPending

  function submit() {
    m.mutate({ id: order.dropship_order_id, reason: reason.trim() }, { onSuccess: onClose })
  }

  return (
    <ModalShell title="Mark as cancelled" order={order}>
      <p className="text-xs text-gray-500">
        The order is removed from active dropships and returned to the queue. The reason is logged
        in the audit trail.
      </p>
      <div className="space-y-1">
        <label htmlFor="cx-reason" className={LABEL}>
          Reason
        </label>
        <textarea
          id="cx-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className={FIELD}
          autoFocus
        />
      </div>
      {m.isError && <ModalError error={m.error} />}
      <ModalFooter
        onClose={onClose}
        onConfirm={submit}
        disabled={!canSubmit}
        confirmLabel={m.isPending ? 'Cancelling…' : 'Cancel dropship'}
        destructive
      />
    </ModalShell>
  )
}

// ---- Mark complete ----

function CompleteModal({ order, onClose }: ModalProps) {
  const m = useCompleteDropship()

  function submit() {
    m.mutate(order.dropship_order_id, { onSuccess: onClose })
  }

  return (
    <ModalShell title="Mark complete" order={order}>
      <p className="text-xs text-gray-500">
        Mark this order complete? It will be removed from the Active board.
      </p>
      {m.isError && <ModalError error={m.error} />}
      <ModalFooter
        onClose={onClose}
        onConfirm={submit}
        disabled={m.isPending}
        confirmLabel={m.isPending ? 'Completing…' : 'Mark complete'}
      />
    </ModalShell>
  )
}
